
import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { DollarSign, TrendingUp, AlertTriangle, PieChart } from "lucide-react";
import { useCostAnalysisOptimized } from "../../cost-analysis/hooks/useCostAnalysisOptimized";
import { useTaskProfitability } from "../../cost-analysis/hooks/useTaskProfitability";

interface BudgetAnalysisDisplayProps {
  budgetAnalysis: any;
  selectedProject?: any;
}

const BudgetAnalysisDisplay = ({ budgetAnalysis, selectedProject }: BudgetAnalysisDisplayProps) => {
  // Use the same hooks as the main Cost Analysis tab for consistency
  const { costAnalysis, isLoading, resourceAssignments, isAllDataFetched } = useCostAnalysisOptimized(selectedProject);
  const { roleBasedCosts, calculatedTotalCost } = useTaskProfitability(
    resourceAssignments,
    selectedProject,
    [30], // Default profit margin
    [12.5], // Default buffer percent
    costAnalysis,
    isAllDataFetched
  );

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Use real data if available, otherwise fall back to estimated data
  const displayData = React.useMemo(() => {
    if (roleBasedCosts.length > 0 && calculatedTotalCost > 0) {
      console.log('✅ BUDGET DISPLAY: Using aligned real data from Cost Analysis tab:', {
        totalCost: calculatedTotalCost,
        roleBreakdown: roleBasedCosts
      });

      // Calculate labor costs from role breakdown
      const laborCosts = roleBasedCosts.reduce((sum, role) => sum + role.amount, 0);
      
      return {
        totalBudget: calculatedTotalCost,
        laborCosts: laborCosts,
        overheadCosts: Math.round(calculatedTotalCost * 0.15), // Estimated 15% overhead
        costBreakdown: roleBasedCosts.reduce((acc, role) => {
          const roleKey = role.category.toLowerCase().replace(' ', '-');
          acc[roleKey] = role.amount;
          return acc;
        }, {} as Record<string, number>),
        recommendations: costAnalysis?.risk_considerations || budgetAnalysis?.recommendations || [
          'Budget is aligned with actual resource assignments',
          'Consider the 12.5% buffer for project contingencies',
          'Monitor actual vs projected costs during project execution'
        ],
        risks: costAnalysis?.risk_considerations || budgetAnalysis?.risks || []
      };
    } else if (budgetAnalysis) {
      console.log('🔄 BUDGET DISPLAY: Using estimated budget analysis');
      return budgetAnalysis;
    } else {
      return null;
    }
  }, [roleBasedCosts, calculatedTotalCost, costAnalysis, budgetAnalysis]);

  if (isLoading || !isAllDataFetched) {
    return (
      <div className="text-center py-12">
        <div className="bg-gradient-to-br from-primary/20 to-green-600/20 p-8 rounded-2xl border-2 border-primary/30">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-300 text-lg font-semibold">Loading aligned budget analysis...</p>
          <p className="text-gray-400 text-sm mt-2">Synchronizing with Cost Analysis tab data.</p>
        </div>
      </div>
    );
  }

  if (!displayData) {
    return (
      <div className="text-center py-12">
        <div className="bg-gradient-to-br from-primary/20 to-green-600/20 p-8 rounded-2xl border-2 border-primary/30">
          <DollarSign className="h-16 w-16 text-primary mx-auto mb-4 animate-bounce" />
          <p className="text-gray-300 text-lg font-semibold">No budget analysis available yet.</p>
          <p className="text-gray-400 text-sm mt-2">Run the AI analysis to get detailed budget recommendations.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Data Source Indicator */}
      {roleBasedCosts.length > 0 && (
        <div className="bg-green-500/10 border border-green-500/20 p-3 rounded-lg">
          <div className="flex items-center gap-2 text-green-300 text-sm">
            <span>Note: Cost analysis estimates shown below. Details are available in the Cost Analysis tab after applying the AI assigned resources to the project.</span>
          </div>
        </div>
      )}

      {/* Budget Overview */}
      <Card className="bg-secondary/30 backdrop-blur-lg border border-primary/20">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            Budget Overview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center bg-primary/10 p-4 rounded-lg border border-primary/20">
              <div className="text-2xl font-bold text-primary mb-1">
                {displayData.totalBudget ? formatCurrency(displayData.totalBudget) : 'TBD'}
              </div>
              <div className="text-xs text-gray-300 uppercase tracking-wide">Total Budget</div>
            </div>
            <div className="text-center bg-green-500/10 p-4 rounded-lg border border-green-500/20">
              <div className="text-2xl font-bold text-green-300 mb-1">
                {displayData.laborCosts ? formatCurrency(displayData.laborCosts) : 'TBD'}
              </div>
              <div className="text-xs text-gray-300 uppercase tracking-wide">Labor Costs</div>
            </div>
            <div className="text-center bg-blue-500/10 p-4 rounded-lg border border-blue-500/20">
              <div className="text-2xl font-bold text-blue-300 mb-1">
                {displayData.overheadCosts ? formatCurrency(displayData.overheadCosts) : 'TBD'}
              </div>
              <div className="text-xs text-gray-300 uppercase tracking-wide">Overhead Costs</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cost Breakdown */}
      {displayData.costBreakdown && (
        <Card className="bg-secondary/30 backdrop-blur-lg border border-primary/20">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              Cost Breakdown by Role
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(displayData.costBreakdown).map(([roleType, cost]: [string, any]) => (
                <div key={roleType} className="flex items-center justify-between p-3 bg-primary/10 rounded-lg border border-primary/20">
                  <div className="flex items-center gap-3">
                    <Badge className="bg-gradient-to-r from-primary to-green-400 text-black">
                      {roleType.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </Badge>
                  </div>
                  <div className="text-white font-semibold">
                    {formatCurrency(cost)}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Budget Recommendations */}
      {displayData.recommendations && (
        <Card className="bg-secondary/30 backdrop-blur-lg border border-primary/20">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              Budget Recommendations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {displayData.recommendations.map((recommendation: string, index: number) => (
                <div key={index} className="flex items-start gap-3 p-3 bg-primary/10 rounded-lg border border-primary/20">
                  <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-gray-300 text-sm">{recommendation}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Budget Risks */}
      {displayData.risks && displayData.risks.length > 0 && (
        <Card className="bg-secondary/30 backdrop-blur-lg border border-red-500/20">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              Budget Risks
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {displayData.risks.map((risk: string, index: number) => (
                <div key={index} className="flex items-start gap-3 p-3 bg-red-500/10 rounded-lg border border-red-500/20">
                  <p className="text-red-200 text-sm">{risk}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BudgetAnalysisDisplay;
